import React, { CSSProperties } from 'react';
import { Flex } from 'antd';
import type {
  SingleShipmentDataExtended,
  ShipmentLegDetail,
} from '@/apis/types';
import StatusTag from './StatusTag';
import { getShipmentLocationParsedAddress } from '@/common/utils/utils';
import type { StatusType } from '../statusType';
import {
  formatCarrierName,
  formatEstimatedArrival,
  textOrEmpty,
} from '../ControlTower/components/shipment/shipmentTableUtils';
import { CustomShipmentText, EllipsisTooltip } from '@/components';
import TeamListWithDots from './TeamListWithDots';
import { sentenceCase } from '@/common/helper';
import { ChevronRightIcon } from 'lucide-react';
import { getShipmentLegModeIcon } from '@/assets/icons/icons';
import { getLegModeIconType } from '../ControlTower/components/shipment/legModeUtils';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

const normalizeStatusText = (value?: unknown): string =>
  String(value ?? '')
    .trim()
    .toLowerCase();

const isDeliveredShipment = (status?: string) =>
  normalizeStatusText(status) === 'delivered';

const trackingPillStyle: CSSProperties = {
  height: 22,
  lineHeight: '20px',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 10,
  fontWeight: 600,
  borderRadius: 4,
};

interface TrackingStatusCardProps {
  legDetail: Partial<SingleShipmentDataExtended> &
    Partial<ShipmentLegDetail> & {
      estimated_delivery_date?: string | null;
      courier_name?: string | null;
    };
  isActive?: boolean;
  onClick?: () => void;
}

const TrackingStatusCard: React.FC<TrackingStatusCardProps> = ({
  legDetail,
  isActive = false,
  onClick,
}) => {
  const { dateFormat } = useUserDateTimeFormat();
  const record = legDetail;
  if (!record) return null;

  const stages =
    typeof record.custom_status === 'object' &&
    !Array.isArray(record.custom_status) &&
    Array.isArray(record.custom_status?.stages)
      ? record.custom_status.stages
      : [];
  const estimatedArrival = formatEstimatedArrival(
    record?.estimated_delivery_date,
    record?.eta_timezone || record?.destination_timezone,
    record?.status,
    dateFormat
  ); //const statusText = textOrEmpty(stage || record.status);
  const isDelivered = isDeliveredShipment(record.status);

  const courierName = record?.courier_name || record?.courier?.title;
  const courierEtaText = [formatCarrierName(courierName), estimatedArrival]
    .filter(Boolean)
    .join(' \u2219 ');
  const referenceIdText = textOrEmpty(record.reference_number);
  const originAddress = getShipmentLocationParsedAddress(record?.from_location);
  const destinationAddress = getShipmentLocationParsedAddress(
    record?.to_location
  );
  const originStr = typeof originAddress === 'string' ? originAddress : '';
  const destinationStr =
    typeof destinationAddress === 'string' ? destinationAddress : '';
  const hasOrigin = Boolean(originStr.trim());
  const hasDestination = Boolean(destinationStr.trim());
  const showArrow = hasOrigin && hasDestination;

  return (
    <div
      className={`tracking-status-card${isActive ? ' is-active' : ''}${
        onClick ? ' is-clickable' : ''
      }`}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? `Open leg ${referenceIdText}` : undefined}
      onClick={onClick}
      onKeyDown={(event) => {
        if (onClick && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          onClick();
        }
      }}
    >
      <Flex
        gap={18}
        align='center'
        justify='space-between'
        style={{ width: '100%', minWidth: 0 }}
      >
        <Flex vertical gap={4} style={{ flex: 1, minWidth: 0 }}>
          {/* <!----top----> */}
          <Flex align='center' wrap='wrap' gap={8}>
            <Flex
              align='center'
              gap={4}
              style={{ maxWidth: '160px', minWidth: 0 }}
            >
              <span
                className='shipment-grid-reference-leg-icon'
                style={{ flexShrink: 0, lineHeight: 0 }}
                aria-hidden='true'
              >
                {getShipmentLegModeIcon(
                  getLegModeIconType(
                    record?.leg_mode?.name ?? record?.leg_mode?.value ?? ''
                  )
                )}
              </span>
              <div style={{ minWidth: 0 }}>
                <EllipsisTooltip
                  text={referenceIdText}
                  className='font-12 fw-600 ellipsis-text-style'
                />
              </div>
            </Flex>

            <span className='shipment-grid-alert-status-wrap'>
              <StatusTag
                type={
                  record.type ? (Number(record.type || 0) as StatusType) : 5
                }
                status={sentenceCase(record?.status)}
                alert_status={record.alert_status as string[]}
                className='shipment-grid-status-tag'
                truncate
                style={trackingPillStyle}
                textStyle={{ fontSize: 10, fontWeight: 600 }}
              />
            </span>
            {record?.teams && (
              <Flex
                gap={4}
                align='center'
                className='shipment-grid-reference-teams'
                style={{ minWidth: 0 }}
              >
                {record?.teams.length > 0 && (
                  <TeamListWithDots teams={record?.teams} />
                )}
              </Flex>
            )}
          </Flex>
          <CustomShipmentText text={courierEtaText} color='var(--text-light)' />
          {(hasOrigin || hasDestination) && (
            <Flex
              align='center'
              gap={8}
              wrap='nowrap'
              className='tracking-card-route'
              style={{ fontSize: '12px', width: '100%', minWidth: 0 }}
            >
              {hasOrigin && (
                <div style={{ minWidth: 0, flexShrink: 1 }}>
                  <EllipsisTooltip
                    text={originStr}
                    className='font-12 fw-400 ellipsis-text-style'
                  />
                </div>
              )}
              {showArrow && <span className='arrow-icon'>→</span>}
              {hasDestination && (
                <div style={{ minWidth: 0, flexShrink: 1 }}>
                  <EllipsisTooltip
                    text={destinationStr}
                    className='font-12 fw-400 ellipsis-text-style'
                  />
                </div>
              )}
            </Flex>
          )}

          {stages.length > 0 && (
            <Flex gap={6} className='tracking-card-stage-row'>
              {stages.map(
                (
                  stageItem: {
                    key?: string;
                    name?: string;
                    is_completed?: boolean;
                  },
                  index: number
                ) => (
                  <span
                    key={`${stageItem?.key || index}`}
                    className={`tracking-card-stage-bar ${
                      isDelivered
                        ? 'is-delivered'
                        : stageItem?.is_completed
                          ? 'is-completed'
                          : ''
                    }`}
                    title={stageItem?.name}
                  />
                )
              )}
            </Flex>
          )}
        </Flex>
        <Flex align='center' justify='center' style={{ flexShrink: 0 }}>
          <ChevronRightIcon size={16} color='var(--text-primary)' />
        </Flex>
      </Flex>
    </div>
  );
};

export default TrackingStatusCard;
